import { useState, useEffect, useRef } from 'react';
import {
	Box,
	Card,
	TextField,
	Button,
	Typography,
	Tabs,
	Tab,
	IconButton,
	Autocomplete,
	Grid,
	Alert
} from '@mui/material';
import {
	Add,
	Delete,
	Save,
	Send,
	ArrowBack
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

import MDButton from '../../components/MDButton';
import DashboardLayout from '../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import apiService from '../../services/api-service';
import GenericFileInput from '../genericFileInput';

const VerbaleEditor = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [tabValue, setTabValue] = useState(0);
	const [loading, setLoading] = useState(false);

	// Area corrente
	const currentAreaId = localStorage.getItem('areaSelected');
	const currentAreaName = localStorage.getItem('areaName');

	// Form data
	const [formData, setFormData] = useState({
		cantiere: currentAreaId ? parseInt(currentAreaId) : null,
		tipologia: null,
		data_riunione: dayjs(),
		luogo: '',
		segretario: null,
		ordine_del_giorno: '',
		stato: 'BOZZA'
	});

	const [partecipanti, setPartecipanti] = useState([]);
	const [allegati, setAllegati] = useState([]);

	// Opzioni select
	const [tipologie, setTipologie] = useState([]);
	const [utentiInterni, setUtentiInterni] = useState([]);

	// Dialog add partecipante
	const [addPartecipanteDialog, setAddPartecipanteDialog] = useState(false);
	const [nuovoPartecipante, setNuovoPartecipante] = useState({
		nome_completo: '',
		email: ''
	});

	// Carica dati se edit mode
	useEffect(() => {
		if (id) {
			fetchVerbale(id);
		}
		fetchTipologie();
		fetchUtentiInterni();
	}, [id]);

	const fetchVerbale = async (verbaleId) => {
		setLoading(true);
		try {
			const response = await apiService.get(`/meetings/verbali/${verbaleId}/`, {}, {});
			setFormData({
				cantiere: response.cantiere,
				tipologia: response.tipologia,
				data_riunione: dayjs(response.data_riunione),
				luogo: response.luogo,
				segretario: response.segretario,
				ordine_del_giorno: response.ordine_del_giorno,
				stato: response.stato
			});
			setPartecipanti(response.partecipanti || []);
			setAllegati(response.allegati || []);
		} catch (error) {
			toast.error('Errore caricamento verbale');
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const fetchTipologie = async () => {
		try {
			const response = await apiService.get('/meetings/tipologie/', {}, {});
			setTipologie(response || []);
		} catch (error) {
			console.error('Errore caricamento tipologie:', error);
		}
	};

	const fetchUtentiInterni = async () => {
		try {
			const response = await apiService.get('/user-internal-management/', {}, {});
			setUtentiInterni(response || []);
		} catch (error) {
			console.error('Errore caricamento utenti:', error);
		}
	};

	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleAddPartecipante = async () => {
		if (!nuovoPartecipante.nome_completo || !nuovoPartecipante.email) {
			toast.error('Nome e email sono obbligatori');
			return;
		}

		const partecipante = {
			verbale: id ? parseInt(id) : null,
			...nuovoPartecipante
		};

		if (id) {
			// Salva su server se verbale già creato
			try {
				const response = await apiService.post('/meetings/partecipanti/', {}, partecipante);
				setPartecipanti([...partecipanti, response]);
				toast.success('Partecipante aggiunto');
			} catch (error) {
				toast.error('Errore aggiunta partecipante');
			}
		} else {
			// Aggiungi solo localmente se verbale non salvato
			setPartecipanti([...partecipanti, { ...partecipante, id: `temp-${Date.now()}` }]);
		}

		setNuovoPartecipante({ nome_completo: '', email: '' });
		setAddPartecipanteDialog(false);
	};

	const handleDeletePartecipante = async (partecipanteId) => {
		if (typeof partecipanteId === 'string' && partecipanteId.startsWith('temp-')) {
			// Rimuovi solo localmente
			setPartecipanti(partecipanti.filter(p => p.id !== partecipanteId));
		} else {
			// Rimuovi dal server
			try {
				await apiService.delete(`/meetings/partecipanti/${partecipanteId}/`, {}, {});
				setPartecipanti(partecipanti.filter(p => p.id !== partecipanteId));
				toast.success('Partecipante rimosso');
			} catch (error) {
				toast.error('Errore rimozione partecipante');
			}
		}
	};

	const handleSalvaBozza = async () => {
		setLoading(true);
		try {
			const payload = {
				cantiere: formData.cantiere,
				tipologia: formData.tipologia,
				data_riunione: formData.data_riunione.hour(12).minute(0).second(0).toISOString(),
				luogo: formData.luogo,
				segretario: formData.segretario,
				ordine_del_giorno: formData.ordine_del_giorno,
				stato: formData.stato
			};

			let verbaleId = id;

			if (id) {
				// Update
				await apiService.patch(`/meetings/verbali/${id}/`, {}, payload);
				toast.success('Verbale aggiornato');
			} else {
				// Create
				const response = await apiService.post('/meetings/verbali/', {}, payload);
				verbaleId = response.id;
				toast.success('Verbale creato');

				// Salva partecipanti
				for (const p of partecipanti) {
					await apiService.post('/meetings/partecipanti/', {}, { ...p, verbale: verbaleId });
				}

				// Redirect a edit mode
				navigate(`/meetings/verbali/${verbaleId}/edit`, { replace: true });
			}
		} catch (error) {
			toast.error('Errore salvataggio verbale');
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleInviaInFirma = async () => {
		// TODO: Cambia stato a IN_FIRMA e invia email
		toast('Funzionalità in fase di implementazione', { icon: '🚧' });
	};

	const handleAllegatiSubmit = async (fileInfos) => {
		if (!id) {
			toast.error('Salva prima il verbale');
			return;
		}

		setLoading(true);

		try {
			const formDataToSend = {
				files: fileInfos
			};

			await apiService.post(`/meetings/verbali/${id}/allegati/`, {}, formDataToSend);
			toast.success('Allegati caricati con successo');

			// Ricarica gli allegati
			const response = await apiService.get(`/meetings/verbali/${id}/`, {}, {});
			setAllegati(response.allegati || []);
		} catch (error) {
			toast.error('Errore caricamento allegati');
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const partecipantiColumns = [
		{ accessorKey: 'nome_completo', header: 'Nome' },
		{ accessorKey: 'email', header: 'Email' }
	];

	const inputFieldsAllegati = [
		{
			type: "textArea",
			name: "descrizione",
			label: "Descrizione",
			required: false
		}
	];

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
					<IconButton onClick={() => navigate('/meetings/verbali')}>
						<ArrowBack />
					</IconButton>
					<Typography variant="h4" sx={{ ml: 2 }}>
						{id ? 'Modifica Verbale' : 'Nuovo Verbale'}
					</Typography>
				</Box>

				{formData.stato !== 'BOZZA' && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Verbale in stato {formData.stato} - Modifica limitata
					</Alert>
				)}

				<Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
					<Tab label="Dati Generali" />
					<Tab label="Partecipanti" />
					<Tab label="Appunti" />
					<Tab label="Allegati" />
				</Tabs>

				{/* TAB 0: Dati Generali */}
				{tabValue === 0 && (
					<Card sx={{ p: 3 }}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Autocomplete
									value={tipologie.find(t => t.id === formData.tipologia) || null}
									onChange={(e, v) => handleInputChange('tipologia', v?.id)}
									options={tipologie}
									getOptionLabel={(option) => option.nome}
									renderInput={(params) => (
										<TextField {...params} label="Tipologia Riunione" required />
									)}
									disabled={formData.stato !== 'BOZZA'}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label="Data Riunione"
										value={formData.data_riunione}
										onChange={(v) => handleInputChange('data_riunione', v)}
										disabled={formData.stato !== 'BOZZA'}
										slotProps={{
											textField: {
												fullWidth: true,
												required: true
											}
										}}
									/>
								</LocalizationProvider>
							</Grid>

							<Grid item xs={12}>
								<TextField
									label="Luogo"
									value={formData.luogo}
									onChange={(e) => handleInputChange('luogo', e.target.value)}
									fullWidth
									required
									disabled={formData.stato !== 'BOZZA'}
								/>
							</Grid>

							<Grid item xs={12}>
								<Autocomplete
									value={utentiInterni.find(u => u.id === formData.segretario) || null}
									onChange={(e, v) => handleInputChange('segretario', v?.id)}
									options={utentiInterni}
									getOptionLabel={(option) => option.name || ''}
									renderInput={(params) => (
										<TextField {...params} label="Segretario" required />
									)}
									disabled={formData.stato !== 'BOZZA'}
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									label="Ordine del Giorno"
									value={formData.ordine_del_giorno}
									onChange={(e) => handleInputChange('ordine_del_giorno', e.target.value)}
									multiline
									rows={6}
									fullWidth
									required
									disabled={formData.stato !== 'BOZZA'}
								/>
							</Grid>
						</Grid>
					</Card>
				)}

				{/* TAB 1: Partecipanti */}
				{tabValue === 1 && (
					<Card sx={{ p: 3 }}>
						<MaterialReactTable
							columns={partecipantiColumns}
							data={partecipanti}
							enableRowActions
							renderRowActions={({ row }) => (
								<IconButton
									onClick={() => handleDeletePartecipante(row.original.id)}
									disabled={formData.stato !== 'BOZZA'}
								>
									<Delete />
								</IconButton>
							)}
						/>

						{formData.stato === 'BOZZA' && (
							<Button
								startIcon={<Add />}
								onClick={() => setAddPartecipanteDialog(true)}
								sx={{ mt: 2 }}
							>
								Aggiungi Partecipante
							</Button>
						)}

						{/* Dialog add partecipante */}
						{addPartecipanteDialog && (
							<Box sx={{ mt: 2, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
								<Typography variant="h6" gutterBottom>Nuovo Partecipante</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<TextField
											label="Nome Completo"
											value={nuovoPartecipante.nome_completo}
											onChange={(e) => setNuovoPartecipante({
												...nuovoPartecipante,
												nome_completo: e.target.value
											})}
											fullWidth
											required
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											label="Email"
											type="email"
											value={nuovoPartecipante.email}
											onChange={(e) => setNuovoPartecipante({
												...nuovoPartecipante,
												email: e.target.value
											})}
											fullWidth
											required
										/>
									</Grid>
									<Grid item xs={12}>
										<Box sx={{ display: 'flex', gap: 2 }}>
											<Button onClick={() => setAddPartecipanteDialog(false)}>
												Annulla
											</Button>
											<MDButton color="info" onClick={handleAddPartecipante}>
												Aggiungi
											</MDButton>
										</Box>
									</Grid>
								</Grid>
							</Box>
						)}
					</Card>
				)}

				{/* TAB 2: Appunti */}
				{tabValue === 2 && (
					<Card sx={{ p: 3 }}>
						<Typography variant="h6" gutterBottom>
							Appunti e Ordine del Giorno
						</Typography>
						<TextField
							label="Descrivi qui gli argomenti trattati nella riunione"
							value={formData.ordine_del_giorno}
							onChange={(e) => handleInputChange('ordine_del_giorno', e.target.value)}
							multiline
							rows={15}
							fullWidth
							disabled={formData.stato !== 'BOZZA'}
							placeholder="Inserisci qui gli appunti della riunione..."
						/>
					</Card>
				)}

				{/* TAB 3: Allegati */}
				{tabValue === 3 && (
					<Card sx={{ p: 3 }}>
						<Typography variant="h6" gutterBottom>
							Allegati (PDF, immagini, documenti)
						</Typography>
					{!id ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							Salva prima il verbale per poter caricare allegati
						</Alert>
					) : (
						<GenericFileInput
							onSubmit={handleAllegatiSubmit}
							authModelName={"VERBALE"}
							inputFields={inputFieldsAllegati}
							files={allegati}
							disableUploadFiles={formData.stato !== 'BOZZA'}
						/>
					)}
					</Card>
				)}

				{/* Actions */}
				<Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
					{formData.stato === 'BOZZA' && (
						<>
							<MDButton
								variant="outlined"
								color="info"
								startIcon={<Save />}
								onClick={handleSalvaBozza}
								disabled={loading}
							>
								Salva Bozza
							</MDButton>

							<MDButton
								variant="contained"
								color="success"
								startIcon={<Send />}
								onClick={handleInviaInFirma}
								disabled={loading || !id}
							>
								Invia in Firma
							</MDButton>
						</>
					)}
				</Box>
			</Box>
		</DashboardLayout>
	);
};

export default VerbaleEditor;
